<template>
  <div>
    <section class="hero is-primary">
      <div class="hero-body">
        <div class="container is-max-hd">
          <div class="columns">
            <div class="column">
              <p class="title is-3 mb-2">
                <span class="patient-name">{{ patientName.toUpperCase() }}</span>
              </p>
              <p class="subtitle patient-summary">
                <template v-if="showSubscriberName">
                  <strong>Subscriber Name:</strong>
                  <span>&nbsp;{{ subscriberName }}</span>
                  <br />
                </template>
                <template v-if="patientRelationship">
                  <strong>{{ patientRelationship }}</strong>
                  <span v-if="selectedPayer?.patient_gender">
                    &nbsp;- {{ selectedPayer.patient_gender }}
                  </span>
                  <span v-if="patientDOB">&nbsp;- {{ formatDate(patientDOB) }}</span>
                  <br />
                </template>
                <span v-if="selectedPayer?.member_id">
                  <strong>MemberID</strong>
                  : {{ selectedPayer?.member_id }}
                </span>
                <br />
                <span>
                  <strong>Facility</strong>
                  : {{ provider }}
                </span>
                <br />
                <span>
                  <strong>Created</strong>
                  : {{ formatDate(blanketVob.created_at) }}
                </span>
                <br />
                <span>
                  <strong>Status</strong>
                  : {{ blanketVob.status }}
                </span>
              </p>
            </div>

            <div class="column has-text-left">
              <p class="subtitle">
                <strong>Patient Address:</strong>
                <br />
                {{ blanketVob.address }}
                <br />
                {{ blanketVob.city }}, {{ blanketVob.state }},
                {{ blanketVob.zip }}
              </p>
              <p class="subtitle" v-if="getPrimaryCareProvider(payers[0])">
                <strong>Primary Care Provider:</strong>
                <br />
                {{ getPrimaryCareProvider(payers[0])?.name }}
                <br />
                {{ getPrimaryCareProvider(payers[0])?.phone }}
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>

    <section class="section">
      <div class="container is-max-hd">
        <div class="columns">
          <div class="column is-one-quarter">
            <div class="box payer-table-container">
              <div class="section">
                <div class="payer-table">
                  <b-table
                    :data="blanketVob.blanket_vob_payers"
                    :loading="false"
                    hoverable
                    :row-class="getPayerRowClass"
                    @row-click="selectedPayer = $event"
                  >
                    <b-table-column field="payer_name" label="Payer" v-slot="props">
                      <a class="payer-link" @click="viewPayerDetails(props.row)">
                        {{ props.row.payer_name }}
                      </a>
                    </b-table-column>

                    <b-table-column field="status" label="Status" v-slot="props">
                      <b-tag :type="getStatusType(props.row.status, true)" size="is-small">
                        {{ props.row.status }}
                      </b-tag>
                    </b-table-column>

                    <b-table-column field="actions" label="Actions" v-slot="props">
                      <b-icon
                        icon="reload"
                        :class="[
                          'return-icon',
                          props.row.status === 'Inactive' ? 'is-disabled' : '',
                        ]"
                        @click.native.stop="
                          props.row.status !== 'Inactive' && handleReRunAsInquiry(props.row)
                        "
                      ></b-icon>
                    </b-table-column>
                  </b-table>
                </div>
              </div>
            </div>

            <div class="box benefit-filter">
              <h5 class="title is-5">Benefits</h5>
              <div class="benefit-list">
                <b-field>
                  <b-checkbox
                    v-model="selectedBenefits"
                    @input="handleBenefitsChange"
                    :native-value="'All'"
                  >
                    <span>All</span>
                  </b-checkbox>
                </b-field>
                <hr class="has-background-primary" />
                <b-field v-for="service in getServiceDetails(payers[0])" :key="service.ServiceName">
                  <b-checkbox
                    v-model="selectedBenefits"
                    @input="handleBenefitsChange"
                    :native-value="service.ServiceName"
                  >
                    <span>{{ service.ServiceName }}</span>
                  </b-checkbox>
                </b-field>
              </div>
            </div>
          </div>
          <div class="column is-three-quarters">
            <div class="action-buttons">
              <b-button
                type="is-primary"
                icon-left="reload"
                :disabled="!selectedPayer || selectedPayer.status?.toLowerCase() !== 'active'"
                @click="handleReRunAsInquiry(selectedPayer)"
              >
                Re Run as Inquiry
              </b-button>
            </div>
            <div class="box">
              <div class="insurance-discovery-detail-plan">
                <div class="section">
                  <div class="section-header">
                    <h3 class="title is-4">{{ selectedPayer?.payer_name?.toUpperCase() }}</h3>
                    <b-tag :type="getPlanStatusType(selectedPayer?.status)" size="is-medium">
                      {{ selectedPayer?.status || 'Unknown' }}
                    </b-tag>
                  </div>

                  <div class="info-grid">
                    <div class="info-row" v-if="selectedPayer?.policy_type">
                      <div class="info-label">Insurance Type</div>
                      <div class="info-value">{{ selectedPayer?.policy_type }}</div>
                    </div>

                    <div class="info-row" v-if="selectedPayer?.plan_name">
                      <div class="info-label">Plan Name</div>
                      <div class="info-value">{{ selectedPayer?.plan_name }}</div>
                    </div>

                    <div class="info-row" v-if="selectedPayer?.plan_number">
                      <div class="info-label">Plan Number</div>
                      <div class="info-value">{{ selectedPayer?.plan_number }}</div>
                    </div>

                    <div class="info-row" v-if="selectedPayer?.group_name">
                      <div class="info-label">Group Name</div>
                      <div class="info-value">{{ selectedPayer?.group_name }}</div>
                    </div>

                    <div class="info-row" v-if="selectedPayer?.group_number">
                      <div class="info-label">Group Number</div>
                      <div class="info-value">{{ selectedPayer?.group_number }}</div>
                    </div>

                    <div class="info-row" v-if="selectedPayer?.plan_network_id">
                      <div class="info-label">Plan Network ID</div>
                      <div class="info-value">{{ selectedPayer?.plan_network_id }}</div>
                    </div>

                    <div class="info-row" v-if="selectedPayer?.plan_network_name">
                      <div class="info-label">Plan Network Name</div>
                      <div class="info-value">{{ selectedPayer?.plan_network_name }}</div>
                    </div>

                    <div class="info-row" v-if="selectedPayer?.last_updated_date_of_edi">
                      <div class="info-label">Last EDI Update</div>
                      <div class="info-value">
                        {{ formatDate(selectedPayer?.last_updated_date_of_edi) }}
                      </div>
                    </div>

                    <div class="info-row" v-if="getCaseNumber(selectedPayer)">
                      <div class="info-label">Case Number</div>
                      <div class="info-value">{{ getCaseNumber(selectedPayer) }}</div>
                    </div>

                    <div class="info-row" v-if="getPriorIdNumber(selectedPayer)">
                      <div class="info-label">Prior ID Number</div>
                      <div class="info-value">{{ getPriorIdNumber(selectedPayer) }}</div>
                    </div>

                    <div class="info-row" v-if="selectedPayer?.effective_date">
                      <div class="info-label">Coverage Start Date</div>
                      <div class="info-value">{{ formatDate(selectedPayer?.effective_date) }}</div>
                    </div>

                    <div class="info-row" v-if="selectedPayer?.expiry_date">
                      <div class="info-label">Coverage End Date</div>
                      <div class="info-value">
                        {{ formatDate(selectedPayer?.expiry_date) || 'N/A' }}
                      </div>
                    </div>
                  </div>

                  <div class="additional-identifications" v-if="payerIdentifications.length">
                    <h5 class="title is-5">Additional Identifications</h5>
                    <div class="info-grid">
                      <div
                        class="info-row"
                        v-for="identification in payerIdentifications"
                        :key="identification.key"
                      >
                        <div class="info-label">{{ identification.label }}</div>
                        <div class="info-value">
                          {{ identification.value }}
                          <span v-if="identification.description">
                            ({{ identification.description }})
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div
                    class="response-information"
                    v-if="isSelectedPayerInactive && payerResponseInformation.length"
                  >
                    <h5 class="title is-5">Response Information</h5>
                    <div class="network-status">
                      <div class="network-details">
                        <div
                          class="network-detail-row"
                          v-for="row in payerResponseInformation"
                          :key="row.key"
                        >
                          <span class="detail-label">{{ row.label }}:</span>
                          <span class="detail-value">{{ row.value }}</span>
                        </div>
                      </div>
                    </div>
                  </div>

                  <!-- Additional Contact Information from payer_plan_response -->
                  <div
                    class="additional-contacts"
                    v-if="
                      getAdditionalPayerContacts(selectedPayer) &&
                      getAdditionalPayerContacts(selectedPayer).length
                    "
                  >
                    <h5 class="title is-5">Additional Contact Information</h5>
                    <div class="contact-list">
                      <div
                        class="contact-item"
                        v-for="(contact, idx) in getAdditionalPayerContacts(selectedPayer)"
                        :key="idx"
                      >
                        <div class="contact-name" v-if="contact.type || contact.name">
                          <span v-if="contact.type">[{{ contact.type }}]</span>
                          <span v-if="contact.name">{{ contact.name }}</span>
                        </div>
                        <div class="contact-phones" v-if="contact.phones && contact.phones.length">
                          <div class="contact-phone" v-for="(phone, j) in contact.phones" :key="j">
                            <template v-if="phone.type">
                              <span class="detail-label">{{ phone.type }}:</span>
                              <span class="detail-value">{{ phone.number }}</span>
                            </template>
                            <template v-else>
                              <span class="detail-value">{{ phone.number }}</span>
                            </template>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div class="service-details-section" v-if="getServiceDetails(selectedPayer)">
              <div
                v-for="service in getServiceDetails(selectedPayer)"
                :key="service.ServiceName"
                class="box"
                v-show="showService(service)"
              >
                <div class="service-header">
                  <p class="title is-4">{{ service.ServiceName }}</p>
                  <b-tag :type="getStatusClass(service)" size="is-medium">
                    {{ getStatusName(service) }}
                  </b-tag>
                </div>

                <!-- Coverage Status -->
                <template
                  v-if="
                    getCoverageStatus(service) &&
                    (getCoverageStatus(service).description ||
                      getCoverageStatus(service).policyType ||
                      getCoverageStatus(service).benefitDescription)
                  "
                >
                  <div class="coverage-status">
                    <div class="status-header">
                      <span v-if="getCoverageStatus(service).description">
                        ({{ getCoverageStatus(service).description }})
                      </span>
                    </div>
                    <div v-if="getCoverageStatus(service).policyType" class="status-detail">
                      Policy Type: {{ getCoverageStatus(service).policyType }}
                    </div>
                    <div v-if="getCoverageStatus(service).benefitDescription" class="status-detail">
                      Benefit Description: {{ getCoverageStatus(service).benefitDescription }}
                    </div>
                  </div>
                </template>

                <hr class="has-background-primary" />

                <!-- In Network Status -->
                <template v-if="getNetworkStatus(service, 'Yes')">
                  <div class="network-status">
                    <p class="network-title">In Plan-Network Status</p>
                    <div class="network-details">
                      <div
                        v-for="(value, key) in getNetworkStatus(service, 'Yes')"
                        :key="key"
                        class="network-detail-row"
                      >
                        <span class="detail-label">{{ key }}:</span>
                        <span class="detail-value">{{ value }}</span>
                      </div>
                    </div>
                  </div>
                </template>

                <!-- Out of Network Status -->
                <template v-if="getNetworkStatus(service, 'No')">
                  <div class="network-status">
                    <p class="network-title">Out of Plan-Network Status</p>
                    <div class="network-details">
                      <div
                        v-for="(value, key) in getNetworkStatus(service, 'No')"
                        :key="key"
                        class="network-detail-row"
                      >
                        <span class="detail-label">{{ key }}:</span>
                        <span class="detail-value">{{ value }}</span>
                      </div>
                    </div>
                  </div>
                </template>

                <!-- Both Network Status -->
                <template v-if="getNetworkStatus(service, 'Both')">
                  <div class="network-status">
                    <p class="network-title">Applies to In & Out Plan-Network Status</p>
                    <div class="network-details">
                      <div
                        v-for="(value, key) in getNetworkStatus(service, 'Both')"
                        :key="key"
                        class="network-detail-row"
                      >
                        <span class="detail-label">{{ key }}:</span>
                        <span class="detail-value">{{ value }}</span>
                      </div>
                    </div>
                  </div>
                </template>

                <template
                  v-if="isSelectedPayerInactive && getOtherNetworkDetails(service).length"
                >
                  <div class="network-status">
                    <p class="network-title">Additional / Non-network Information</p>
                    <div
                      class="eligibility-detail"
                      v-for="(detail, detailIndex) in getOtherNetworkDetails(service)"
                      :key="`${detail.EligibilityOrBenefit || 'detail'}-${detailIndex}`"
                    >
                      <p class="eligibility-detail-title">
                        {{ detail.EligibilityOrBenefit || 'Additional Information' }}
                      </p>

                      <div
                        class="network-details"
                        v-if="getEligibilityDetailRows(detail).length"
                      >
                        <div
                          class="network-detail-row"
                          v-for="row in getEligibilityDetailRows(detail)"
                          :key="row.key"
                        >
                          <span class="detail-label">{{ row.label }}:</span>
                          <span class="detail-value">{{ row.value }}</span>
                        </div>
                      </div>

                      <div class="detail-subsection" v-if="detail.Message?.length">
                        <p class="detail-subsection-title">Payer Messages</p>
                        <p
                          class="detail-message"
                          v-for="(message, messageIndex) in detail.Message"
                          :key="messageIndex"
                        >
                          {{ message }}
                        </p>
                      </div>

                      <div
                        class="detail-subsection"
                        v-if="getVisibleBenefitEntities(detail).length"
                      >
                        <p class="detail-subsection-title">Related Entities</p>
                        <div
                          class="contact-item detail-entity"
                          v-for="(entity, entityIndex) in getVisibleBenefitEntities(detail)"
                          :key="entityIndex"
                        >
                          <div class="contact-name">
                            {{ entity.EntityType || 'Entity' }}
                          </div>
                          <div
                            class="network-details"
                            v-if="getBenefitEntityRows(entity).length"
                          >
                            <div
                              class="network-detail-row"
                              v-for="row in getBenefitEntityRows(entity)"
                              :key="row.key"
                            >
                              <span class="detail-label">{{ row.label }}:</span>
                              <span class="detail-value">{{ row.value }}</span>
                            </div>
                          </div>
                        </div>
                      </div>

                      <div
                        class="detail-subsection"
                        v-if="detail.HealthCareServiceDeliveries?.length"
                      >
                        <p class="detail-subsection-title">Service Delivery</p>
                        <div
                          class="contact-item detail-entity"
                          v-for="(delivery, deliveryIndex) in detail.HealthCareServiceDeliveries"
                          :key="deliveryIndex"
                        >
                          <div class="network-details">
                            <div
                              class="network-detail-row"
                              v-for="row in getServiceDeliveryRows(delivery)"
                              :key="row.key"
                            >
                              <span class="detail-label">{{ row.label }}:</span>
                              <span class="detail-value">{{ row.value }}</span>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </template>

                <p v-if="!hasServiceDetails(service)" class="no-details">
                  No service details returned from the payer
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  </div>
</template>

<script>
import BTag from 'buefy/src/components/tag/Tag';

export default {
  name: 'BlanketVOBDetailPlan',
  components: {
    BTag,
  },
  props: {
    blanketVob: {
      type: Object,
      required: true,
    },
    created_at: {
      type: String,
      required: false,
      default: null,
    },
    as_of_date: {
      type: String,
      required: false,
      default: null,
    },
    blanket_vob_id: {
      type: String,
      required: false,
      default: null,
    },
    provider: {
      type: String,
      required: true,
    },
    payers: {
      type: Array,
      required: true,
    },
    detailsRouteName: {
      type: String,
      required: false,
      default: 'BlanketVOBDetails',
    },
  },
  data() {
    return {
      selectedBenefits: ['All'],
      selectedPayer: null,
    };
  },
  mounted() {
    if (this.payers && this.payers.length > 0) {
      this.selectedPayer = this.payers[0];
    }
  },
  watch: {
    payers(newPayers) {
      if (!this.selectedPayer && newPayers && newPayers.length > 0) {
        this.selectedPayer = newPayers[0];
      }
    },
  },
  computed: {
    patientName() {
      return this.blanketVob.full_name || '';
    },
    patientRelationship() {
      if (!this.selectedPayer) return '';

      const relationship = this.selectedPayer.subscriber_relationship?.trim();
      return !relationship || relationship === 'Self' ? 'Subscriber' : relationship;
    },
    patientDOB() {
      return this.blanketVob.dob || this.selectedPayer?.dob;
    },
    subscriberName() {
      return this.getSubscriberName(this.selectedPayer);
    },
    showSubscriberName() {
      const relationship = this.selectedPayer?.subscriber_relationship?.trim().toLowerCase();
      return Boolean(
        this.subscriberName && relationship && !['self', 'subscriber'].includes(relationship)
      );
    },
    payerIdentifications() {
      return this.getPayerIdentifications(this.selectedPayer);
    },
    isSelectedPayerInactive() {
      return this.selectedPayer?.status?.toLowerCase() === 'inactive';
    },
    payerResponseInformation() {
      return this.getPayerResponseInformation(this.selectedPayer);
    },
  },
  methods: {
    formatDate(date) {
      if (!date) return '-';
      return new Date(date).toLocaleDateString();
    },
    getSubscriberName(payer) {
      const payerName = (payer?.full_name || '').toString().trim();
      if (payerName) return payerName;
      if (!payer?.payer_plan_response) return '';

      try {
        const subscriber = JSON.parse(payer.payer_plan_response)?.DemographicInfo?.Subscriber;
        if (!subscriber) return '';

        const fullName = (subscriber.FullName || '').toString().trim();
        if (fullName) return fullName;

        return [
          subscriber.Firstname,
          subscriber.Middlename,
          subscriber.Lastname_R,
          subscriber.Suffix,
        ]
          .map((value) => (value || '').toString().trim())
          .filter(Boolean)
          .join(' ');
      } catch (e) {
        return '';
      }
    },
    getStatusType(status) {
      const types = {
        active: 'is-success',
        inactive: 'is-danger',
        pending: 'is-warning',
      };
      return types[status?.toLowerCase()] || 'is-grey';
    },
    getPlanStatusType(status) {
      const types = {
        active: 'is-success',
        inactive: 'is-danger',
        pending: 'is-warning',
      };
      return types[status?.toLowerCase()] || 'is-grey';
    },
    getCaseNumber(payer) {
      if (!payer?.payer_plan_response) return null;
      try {
        const response = JSON.parse(payer.payer_plan_response);
        const identification = response?.DemographicInfo?.Subscriber?.Identification || [];
        const caseNumber = identification.find((id) => id.Type === 'Case Number');
        return caseNumber?.Code || null;
      } catch (e) {
        return null;
      }
    },
    getPriorIdNumber(payer) {
      if (!payer?.payer_plan_response) return null;
      try {
        const response = JSON.parse(payer.payer_plan_response);
        const identification = response?.DemographicInfo?.Subscriber?.Identification || [];
        const priorId = identification.find((id) => id.Type === 'Prior ID Number');
        return priorId?.Code || null;
      } catch (e) {
        return null;
      }
    },
    getPrimaryCareProvider(payer) {
      if (!payer?.payer_plan_response) return null;
      try {
        const response = JSON.parse(payer.payer_plan_response);
        const pcpInfo = response?.PCPAuthInfoSummary;
        if (!pcpInfo?.PrimaryCareProviderName) return null;

        return {
          name: pcpInfo.PrimaryCareProviderName,
          phone: pcpInfo.PrimaryCareProviderPhoneNumber,
        };
      } catch (e) {
        return null;
      }
    },
    getAdditionalPayerContacts(payer) {
      if (!payer?.payer_plan_response) return [];
      try {
        const response = JSON.parse(payer.payer_plan_response);
        const services = response?.ServiceDetails || [];
        const contactsMap = new Map();
        services.forEach((service) => {
          (service.EligibilityDetails || []).forEach((detail) => {
            this.getVisibleBenefitEntities(detail).forEach((entity) => {
              const name = entity.Lastname_R || entity.Firstname || null;
              const type = entity.EntityType || null;
              const phoneObjs = (entity.CommunicationNumber || [])
                .filter((c) => c && c.Number)
                .map((c) => ({ type: c.Type || null, number: c.Number }));
              if (!phoneObjs.length) return;

              const normalizedType = (type || '').toString().trim().toLowerCase();
              const normalizedName = (name || '').toString().trim().toLowerCase();
              const keyBase = `${normalizedType}|${normalizedName}`;
              const key = normalizedName ? keyBase : `${keyBase}|${phoneObjs[0].number}`;

              if (!contactsMap.has(key)) {
                contactsMap.set(key, { name, type, phones: [] });
              }
              const contact = contactsMap.get(key);
              if (!contact.name && name) contact.name = name;
              if (!contact.type && type) contact.type = type;

              phoneObjs.forEach((p) => {
                const existingIdx = contact.phones.findIndex((x) => x.number === p.number);
                if (existingIdx === -1) {
                  contact.phones.push({ type: p.type || null, number: p.number });
                } else if (!contact.phones[existingIdx].type && p.type) {
                  contact.phones[existingIdx].type = p.type;
                }
              });
            });
          });
        });
        return Array.from(contactsMap.values());
      } catch (e) {
        return [];
      }
    },
    getPayerIdentifications(payer) {
      if (!payer?.payer_plan_response) return [];
      try {
        const response = JSON.parse(payer.payer_plan_response);
        const identifications = new Map();

        (response?.ServiceDetails || []).forEach((service) => {
          (service.EligibilityDetails || []).forEach((detail) => {
            (detail.Identifications || []).forEach((identification) => {
              const type = (identification.Type || '').toString().trim();
              const name = (identification.Name || '').toString().trim();
              const code = (identification.Code || '').toString().trim();
              if (!type && !name && !code) return;

              const key = JSON.stringify([type, name, code]);
              if (identifications.has(key)) return;
              const label = type && (code || name) ? type : (code && name) || 'Identification';

              identifications.set(key, {
                key,
                label,
                value: code || name || type,
                description: code && type && name && name !== type && name !== code ? name : '',
              });
            });
          });
        });

        return Array.from(identifications.values());
      } catch (e) {
        return [];
      }
    },
    getPayerResponseInformation(payer) {
      if (!payer?.payer_plan_response) return [];
      try {
        const response = JSON.parse(payer.payer_plan_response);
        const apiResponseMessage = (response?.APIResponseMessage || '').toString().trim();

        return this.buildDetailRows([
          ['API Response', apiResponseMessage.toLowerCase() === 'processed' ? '' : apiResponseMessage],
          ['Additional Information', response?.AddtionalInfo],
          ['Exception Notes', response?.ExceptionNotes],
        ]);
      } catch (e) {
        return [];
      }
    },
    buildDetailRows(items) {
      return items
        .map(([label, value], index) => ({
          key: `${label}-${index}`,
          label,
          value: value === null || value === undefined ? '' : value.toString().trim(),
        }))
        .filter((row) => row.value);
    },
    getOtherNetworkDetails(service) {
      const standardNetworkIndicators = ['Yes', 'No', 'Both'];
      return (service?.EligibilityDetails || []).filter(
        (detail) => !standardNetworkIndicators.includes(detail.PlanNetworkIndicator)
      );
    },
    getEligibilityDetailRows(detail) {
      const rows = [
        ['Network', detail.PlanNetworkIndicator],
        ['Coverage Level', detail.CoverageLevel],
        ['Coverage Description', detail.PlanCoverageDescription],
        ['Insurance Type', detail.InsuranceType],
        ['Amount', detail.MonetaryAmount],
        ['Percentage', detail.Percent],
        ['Authorization/Certification Required', detail.AuthorizationOrCertificationRequired],
        ['Quantity', detail.Quantity],
        ['Quantity Qualifier', detail.QuantityQualifier],
        ['Time Period', detail.TimePeriodQualifier],
        ['Place of Service', detail.PlaceOfService],
        ['Procedure', detail.Procedure],
      ];

      (detail.Date || []).forEach((date) => {
        rows.push([date.Type || 'Date', date.Date]);
      });

      return this.buildDetailRows(rows);
    },
    getBenefitEntityRows(entity) {
      const name = [entity.Firstname, entity.Middlename, entity.Lastname_R, entity.Suffix]
        .filter(Boolean)
        .join(' ');
      const address = [entity.Address1, entity.Address2].filter(Boolean).join(', ');
      const cityStateZip = [
        entity.City,
        [entity.State, entity.Zip].filter(Boolean).join(' '),
      ]
        .filter(Boolean)
        .join(', ');
      const rows = [
        ['Name', name],
        ['Provider Taxonomy', entity.ProviderTaxonomy],
        ['Address', address],
        ['City, State ZIP', cityStateZip],
      ];

      (entity.CommunicationNumber || []).forEach((communication) => {
        rows.push([communication.Type || 'Phone', communication.Number]);
      });

      return this.buildDetailRows(rows);
    },
    getVisibleBenefitEntities(detail) {
      return (detail?.BenefitEntities || []).filter(
        (entity) => (entity?.EntityType || '').toString().trim().toLowerCase() !== 'provider'
      );
    },
    getServiceDeliveryRows(delivery) {
      return this.buildDetailRows([
        ['Time Period', delivery.TimePeriodQualifier],
        ['Total Quantity', delivery.TotalQuantity],
        ['Delivery Frequency', delivery.DeliveryFrequency],
        ['Quantity Qualifier', delivery.QuantityQualifier],
        ['Total Number of Periods', delivery.TotalNumberOfPeriods],
        ['Measurement Period', delivery.MeasurementPeriod],
        ['Measurement Unit', delivery.MeasurementUnit],
        ['Delivery Pattern Time', delivery.DeliveryPatternTime],
      ]);
    },
    getServiceDetails(payer) {
      if (!payer?.payer_plan_response) return null;
      try {
        const response = JSON.parse(payer.payer_plan_response);
        const services = response?.ServiceDetails || [];
        return services.sort((a, b) => {
          if (a.ServiceName === 'Health Benefit Plan Coverage') return -1;
          if (b.ServiceName === 'Health Benefit Plan Coverage') return 1;
          return 0;
        });
      } catch (e) {
        return null;
      }
    },
    getCoverageStatus(service) {
      const activeCoverage = service.EligibilityDetails.find(
        (detail) =>
          detail.EligibilityOrBenefit === 'Active Coverage' ||
          detail.EligibilityOrBenefit === 'Inactive'
      );

      if (!activeCoverage) return null;

      const benefitDesc = service.EligibilityDetails.find(
        (detail) => detail.EligibilityOrBenefit === 'Benefit Description'
      );

      return {
        type: activeCoverage.EligibilityOrBenefit,
        description: activeCoverage.PlanCoverageDescription,
        policyType: activeCoverage.InsuranceType,
        benefitDescription: benefitDesc?.Message?.[0],
      };
    },
    getNetworkStatus(service, networkType) {
      const details = service.EligibilityDetails.filter(
        (detail) => detail.PlanNetworkIndicator === networkType
      );

      if (!details.length) return null;

      const status = {};

      details.forEach((detail) => {
        // Handle Co-Insurance
        if (detail.EligibilityOrBenefit === 'Co-Insurance') {
          status[`Individual Co-Insurance`] = `${(parseFloat(detail.Percent) * 100).toFixed(0)}%`;
          if (detail.AuthorizationOrCertificationRequired) {
            status[`Authorization/Certification Required`] =
              detail.AuthorizationOrCertificationRequired;
          }
          if (Array.isArray(detail.Date)) {
            if (detail.Date[0]?.Date) status[`Benefit`] = detail.Date[0].Date;
            if (detail.Date[1]?.Date) status[`Benefit Start Date`] = detail.Date[1].Date;
            if (detail.Date[2]?.Date) status[`Benefit End Date`] = detail.Date[2].Date;
          }
          if (detail.Message?.length) {
            detail.Message.forEach((msg) => {
              status[`*** ${msg}`] = '';
            });
          }
        }

        // Handle Deductible
        if (detail.EligibilityOrBenefit === 'Deductible') {
          status[`Individual Calendar Year Deductible`] = `$${detail.MonetaryAmount}`;
          if (Array.isArray(detail.Date) && detail.Date[0]?.Date) {
            status[`Plan`] = detail.Date[0].Date;
          }
        }

        // Handle Out of Pocket
        if (detail.EligibilityOrBenefit === 'Out of Pocket') {
          status[`Individual Calendar Year Out of Pocket`] = `$${detail.MonetaryAmount}`;
          if (Array.isArray(detail.Date)) {
            if (detail.Date[0]?.Date) status[`Benefit`] = detail.Date[0].Date;
            if (detail.Date[1]?.Date) status[`Benefit Start Date`] = detail.Date[1].Date;
            if (detail.Date[2]?.Date) status[`Benefit End Date`] = detail.Date[2].Date;
          }
        }

        // Handle Benefit Description
        if (detail.EligibilityOrBenefit === 'Benefit Description') {
          if (detail.Message?.length) {
            detail.Message.forEach((msg) => {
              status[`*** ${msg}`] = '';
            });
          }
          if (Array.isArray(detail.Date)) {
            if (detail.Date[0]?.Date) status[`Benefit`] = detail.Date[0].Date;
            if (detail.Date[1]?.Date) status[`Benefit Start Date`] = detail.Date[1].Date;
            if (detail.Date[2]?.Date) status[`Benefit End Date`] = detail.Date[2].Date;
          }
        }

        // Handle UMO Information
        if (detail.BenefitEntities?.length) {
          this.getVisibleBenefitEntities(detail).forEach((entity) => {
            if (entity.EntityType === 'Utilization Management Organization') {
              status[`[Utilization Management Organization]`] = '';
              if (entity.Lastname_R) status[`Last Name`] = entity.Lastname_R;
              if (entity.CommunicationNumber?.length) {
                status[`Telephone`] = entity.CommunicationNumber[0].Number;
              }
            }
          });
        }
      });

      return Object.keys(status).length ? status : null;
    },
    getStatusName(service) {
      const activeCoverage = service.EligibilityDetails.find(
        (detail) =>
          detail.EligibilityOrBenefit === 'Active Coverage' ||
          detail.EligibilityOrBenefit === 'Inactive'
      );
      return activeCoverage?.EligibilityOrBenefit || 'Unknown';
    },
    getStatusClass(service) {
      const status = this.getStatusName(service);
      if (status === 'Active Coverage') {
        return 'is-success';
      }
      if (status === 'Inactive') {
        return 'is-danger';
      }
      return 'is-info';
    },
    hasServiceDetails(service) {
      return service.EligibilityDetails && service.EligibilityDetails.length > 0;
    },
    handleBenefitsChange() {
      if (this.allIsFirstItem(this.selectedBenefits)) {
        this.selectedBenefits = this.selectedBenefits.filter((i) => i !== 'All');
      } else if (this.allIsLastItem(this.selectedBenefits)) {
        this.selectedBenefits = this.selectedBenefits.filter((i) => i === 'All');
      }
    },
    allIsFirstItem(newValue) {
      return newValue.length > 1 && newValue[0] === 'All';
    },
    allIsLastItem(newValue) {
      return newValue.length > 1 && newValue[newValue.length - 1] === 'All';
    },
    showService(service) {
      if (this.selectedBenefits.includes('All')) {
        return true;
      }
      return this.selectedBenefits.includes(service.ServiceName);
    },
    handleReRunAsInquiry(payer) {
      if (!payer || !this.blanketVob) return;

      this.$router.push({
        name: 'BlanketVOBAsInquiry',
        params: {
          id: this.blanketVob.id,
        },
        query: {
          payerResponseId: payer.id,
        },
      });
    },
    getPayerRowClass(payer) {
      return payer.id === (this.selectedPayer && this.selectedPayer.id) ? 'is-selected' : '';
    },
    viewPayerDetails(payer) {
      this.selectedPayer = payer;
      this.$router.push({
        name: this.detailsRouteName,
        params: {
          id: this.blanketVob.id,
          payerResponseId: payer.id,
        },
      });
    },
  },
};
</script>

<style scoped>
.insurance-discovery-detail-plan {
  background: white;
  border-radius: 8px;
  margin: 0;
}

.section {
  margin-bottom: 0.5rem;
}

.section-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 0.5rem;
  padding-top: 0;
}

.title {
  margin: 0;
  color: #2c3e50;
  font-size: 1.25rem;
  font-weight: 600;
}

.info-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  gap: 1rem;
}

.info-row {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
}

.info-label {
  font-size: 0.875rem;
  color: #666;
  font-weight: 700;
}

.info-value {
  font-size: 1rem;
  color: #2c3e50;
  font-weight: 400;
}

/* Tag styles */
:deep(.tag) {
  font-weight: 500;
  padding: 0.25em 0.75em;
}

:deep(.tag.is-success) {
  background-color: #48c774;
  color: white;
}

:deep(.tag.is-danger) {
  background-color: #f14668;
  color: white;
}

:deep(.tag.is-warning) {
  background-color: #ffdd57;
  color: rgba(0, 0, 0, 0.7);
}

:deep(.tag.is-grey) {
  background-color: #f5f5f5;
  color: #7a7a7a;
}

.hero.is-primary {
  background-color: #7957d4;
}

.subtitle {
  margin-top: 0;
  color: rgba(255, 255, 255, 0.9);
}

.patient-summary {
  margin-top: 0 !important;
}

.box {
  padding: 0.25rem;
  background: white;
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.title.is-3 {
  margin-bottom: 0.5rem !important;
}

.patient-name {
  font-size: 1.75rem;
  font-weight: 600;
  color: white;
  display: block;
  margin-bottom: 0.5rem;
}

.service-details-section {
  margin-top: 2rem;
  padding-left: 0;
}

.service-details-section .box {
  padding: 1.5rem;
  margin-bottom: 1.5rem;
  width: 100%;
}

.service-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1rem;
}

.service-header .title {
  margin: 0;
  color: #2c3e50;
  font-size: 1.25rem;
  font-weight: 600;
}

.coverage-status {
  padding: 1rem 0;
  margin-bottom: 1rem;
}

.status-header {
  font-size: 1.1rem;
  margin-bottom: 0.5rem;
  color: #2c3e50;
}

.status-detail {
  margin: 0.5rem 0;
  font-size: 0.95rem;
  color: #4a4a4a;
}

.network-status {
  background: #f9f9f9;
  padding: 1rem;
  border-radius: 4px;
  margin-bottom: 1rem;
}

.network-title {
  color: #7a7a7a;
  font-size: 0.95rem;
  font-weight: 600;
  margin-bottom: 0.75rem;
}

.network-details {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.network-detail-row {
  display: flex;
  gap: 0.5rem;
  font-size: 0.95rem;
  color: #4a4a4a;
}

.detail-label {
  font-weight: 600;
  min-width: 200px;
}

.detail-value {
  flex: 1;
}

.no-details {
  color: #7a7a7a;
  font-style: italic;
  text-align: center;
  padding: 1rem;
}

hr.has-background-primary {
  height: 2px;
  margin: 1rem 0;
  opacity: 0.2;
}

.benefit-filter {
  padding: 1.5rem;
}

.benefit-filter .title {
  margin-bottom: 1.5rem;
  color: #2c3e50;
  font-size: 1.25rem;
  font-weight: 600;
}

.benefit-list {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
}

.benefit-list hr {
  margin: 0.5rem 0;
  opacity: 0.2;
}

.benefit-list :deep(.checkbox) {
  padding: 0.25rem 0;
}

.benefit-list :deep(.checkbox:hover) {
  background-color: #f5f5f5;
  border-radius: 4px;
}

.benefit-list :deep(.checkbox input[type='checkbox'] + .check) {
  border-color: #7957d5;
}

.benefit-list :deep(.checkbox input[type='checkbox']:checked + .check) {
  background-color: #7957d5;
  border-color: #7957d5;
}

.action-buttons {
  margin-bottom: 1.5rem;
  display: flex;
  justify-content: flex-end;
}

.action-buttons .button {
  font-weight: 500;
  padding: 0.5rem 1rem;
}

.payer-table-container {
  margin-bottom: 0.75rem;
  padding: 0.5rem;
  background: white;
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  border-top: 4px solid #7956d4;
}

.payer-table-container .section {
  margin-bottom: 0.25rem;
  padding: 0 0.5rem;
}

.payer-table-container .section-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 0.25rem;
  padding-top: 0;
}

.payer-table-container .title {
  margin: 0;
  color: #2c3e50;
  font-size: 1.1rem;
  font-weight: 600;
}

.payer-table {
  margin-bottom: 0;
  width: 100%;
}

.payer-table :deep(.table) {
  margin-bottom: 0;
  border: none;
  width: 100%;
}

.payer-table :deep(.table-wrapper) {
  width: 100%;
}

.payer-table :deep(.table td) {
  padding: 0.35rem 0.5rem;
  border: none;
  width: auto;
}

.payer-table :deep(.table th) {
  padding: 0.35rem 0.5rem;
  font-weight: 600;
  color: #2c3e50;
  border: none;
  background: #f8f9fa;
  font-size: 1.1rem;
  width: auto;
}

.payer-table :deep(.table th:first-child),
.payer-table :deep(.table td:first-child) {
  width: 50%;
}

.payer-table :deep(.table th:nth-child(2)),
.payer-table :deep(.table td:nth-child(2)) {
  width: 30%;
}

.payer-table :deep(.table th:last-child),
.payer-table :deep(.table td:last-child) {
  width: 20%;
  text-align: center;
}

.payer-table :deep(.table tr:hover) {
  background-color: #f5f5f5;
}

.payer-table :deep(.table tr.is-selected) {
  background-color: #f5f5f5;
  font-weight: 600;
}

.payer-table :deep(.table tr.is-selected td) {
  color: #3273dc;
}

.payer-table :deep(.tag) {
  font-size: 0.8rem;
  padding: 0.2em 0.5em;
}

.payer-table :deep(.icon) {
  font-size: 1rem;
}

.payer-link {
  color: #3273dc;
  cursor: pointer;
  text-decoration: none;
}

.payer-link:hover {
  text-decoration: underline;
}

.return-icon {
  cursor: pointer;
  color: #3273dc;
}

.return-icon:hover {
  color: #2366d1;
}

.return-icon.is-disabled {
  cursor: not-allowed;
  color: #7a7a7a;
  opacity: 0.8;
}

.return-icon.is-disabled:hover {
  color: #7a7a7a;
}

/* Additional information styles */
.response-information,
.additional-identifications,
.additional-contacts {
  margin-top: 1rem;
}
.response-information .title,
.additional-identifications .title,
.additional-contacts .title {
  margin-bottom: 0.5rem;
}
.eligibility-detail + .eligibility-detail {
  border-top: 1px solid #ddd;
  margin-top: 1rem;
  padding-top: 1rem;
}
.eligibility-detail-title,
.detail-subsection-title {
  color: #4a4a4a;
  font-weight: 600;
  margin-bottom: 0.5rem;
}
.detail-subsection {
  margin-top: 0.75rem;
}
.detail-message {
  margin: 0.25rem 0;
  white-space: pre-wrap;
}
.detail-entity {
  margin-top: 0.5rem;
}
.contact-list {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}
.contact-item {
  background: #f9f9f9;
  padding: 0.75rem;
  border-radius: 4px;
}
.contact-name {
  font-weight: 600;
  margin-bottom: 0.25rem;
}
.contact-phone {
  display: flex;
  gap: 0.5rem;
}
</style>
